import uniq from 'lodash/uniq'
import head from 'lodash/head'
import minBy from 'lodash/minBy'
import maxBy from 'lodash/maxBy'

import wcs from '@/scripts/libs/wcs'
import {
  ALL_PROMOS_LABEL,
  FLASH_SALE_LABEL,
  SCARCITY_PROMO,
  SCARCITY,
} from '@/scripts/constants/promos'
import WcsPromoToggles from './wcs/promo-toggles'
import { WcsPromoToggle } from './wcs/types/plp-promo-toggles'
import AlgoliaPromoToggles from '@/scripts/libs/algolia/promo-toggles'
import { WcsUser } from './wcs/user'
import { PromoToggle } from '@/types/plp'

type PromoTogglesSupportMap = Record<string, Array<PromoToggle>>

export interface PromoToggleHelper {
  getPromoToggles(): Promise<Array<PromoToggle>>
}

class PromoToggles implements PromoToggleHelper {
  public hasPromotion: boolean | undefined
  mergePromoToggles = (promoToggles: PromoToggle[]): PromoToggle[] => {
    const allPstPromotions = promoToggles.filter((promo) => {
      return !promo.isFlash
    })

    const mergedPstPromoToggles = this.mergeSimilarPstPromoTogglesIntoOne(allPstPromotions)

    const allScarcityPromoToggles = promoToggles.filter((promo) => {
      return promo.isFlash
    })
    if (allScarcityPromoToggles.length > 0) {
      const singleScarcityPromoToggle = this.mapSingleScarcityPromoToggle(allScarcityPromoToggles)

      mergedPstPromoToggles.push(singleScarcityPromoToggle)
    }

    return mergedPstPromoToggles
  }

  getTogglesWithUserCluster = (promotions: PromoToggle[]): PromoToggle[] => {
    const userCluster = WcsUser.getMergedUserCluster()
    const filtered = promotions.filter(
      (promo) =>
        !promo.userSegments ||
        promo.userSegments.length === 0 ||
        userCluster?.some((cluster) => promo?.userSegments?.includes(cluster))
    )
    return filtered
  }

  getPromoToggles = async (): Promise<Array<PromoToggle>> => {
    const { searchProvider } = wcs.getStoreConfig()

    if (searchProvider === 'algolia') {
      return AlgoliaPromoToggles.getPromoToggles()
    }

    return WcsPromoToggles.getPromoToggles()
  }

  sortPromotions = (originalOrder: string[], promoToggles: PromoToggle[]): PromoToggle[] => {
    const { searchProvider } = wcs.getStoreConfig()

    if (searchProvider === 'algolia') {
      return AlgoliaPromoToggles.sortPromotions(originalOrder, promoToggles)
    }

    return WcsPromoToggles.sortPromotions(originalOrder, promoToggles)
  }

  mergeSimilarPstPromoTogglesIntoOne = (promoToggles: PromoToggle[]): PromoToggle[] => {
    const supportMap: PromoTogglesSupportMap =
      this.generateSupportMapForMergedPstToggles(promoToggles)

    const mergedPromoToggles = Object.values(supportMap).map((supportToggles) => {
      if (supportToggles.length > 1) {
        const entryValues = supportToggles.flatMap((supportToggle) => [...supportToggle.entryValue])
        const uniqEntryValues = uniq(entryValues)

        const mergedPromoToggle = head(supportToggles)
        if (mergedPromoToggle) {
          mergedPromoToggle.entryValue = uniqEntryValues
        }
        return mergedPromoToggle
      } else {
        return head(supportToggles) as PromoToggle
      }
    })

    const mergedPromoToggleWithUniqueEntryValues = mergedPromoToggles.map((promoToggle) => {
      return {
        ...promoToggle,
        entryValue: uniq(promoToggle?.entryValue),
      }
    })

    return mergedPromoToggleWithUniqueEntryValues
  }

  private generateSupportMapForMergedPstToggles = (
    promoToggles: PromoToggle[]
  ): PromoTogglesSupportMap => {
    const supportMap: PromoTogglesSupportMap = {}
    promoToggles.forEach((promoToggle) => {
      const isKeyPresentInMap = Object.keys(supportMap).includes(promoToggle.badge as string)
      if (isKeyPresentInMap) {
        supportMap[promoToggle.badge as string].push(promoToggle)
      } else {
        supportMap[promoToggle.badge as string] = [promoToggle]
      }
    })
    return supportMap
  }

  private mapSingleScarcityPromoToggle = (
    allScarcityPromotionsInProgress: PromoToggle[]
  ): PromoToggle => {
    const entryValue = allScarcityPromotionsInProgress.flatMap((promo) => promo.entryValue)

    const earliestToggle = minBy(allScarcityPromotionsInProgress, 'startDate')
    const latestToggle = maxBy(allScarcityPromotionsInProgress, 'endDate')
    return {
      id: SCARCITY_PROMO,
      dataElID: 'X_SunPLP_0-Filter_FlashSales',
      badge: head(allScarcityPromotionsInProgress)?.badge ?? '',
      isFlash: true,
      entryValue,
      icon: '',
      endDateTimestamp: latestToggle?.endDateTimestamp ?? 0,
      name: earliestToggle?.name ?? '',
      startDateTimestamp: earliestToggle?.startDateTimestamp ?? 0,
      startDate: `${earliestToggle?.startDateTimestamp}` ?? '',
      endDate: `${latestToggle?.endDateTimestamp}` ?? 0,
      type: SCARCITY,
      userSegments: '',
    }
  }

  mapPromotions = (
    promotions: WcsPromoToggle[],
    allPromoLabel = ALL_PROMOS_LABEL,
    flashSalesLabel = FLASH_SALE_LABEL,
    allPromoToggleVisibility: string
  ) => {
    return WcsPromoToggles.mapPromotions(
      promotions,
      allPromoLabel,
      flashSalesLabel,
      allPromoToggleVisibility
    )
  }
  public setHasPromotion(value: boolean): void {
    this.hasPromotion = value
  }
}

export default new PromoToggles()
